import { concat, defer, EMPTY, from, merge, Observable, of } from 'rxjs';
import { mergeMap, pluck, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { execute as getTokenAjax } from '@wearejh/swagger-rxjs/lib/PostV1IntegrationCustomerToken';
import { execute as restCustomerMe } from '@wearejh/swagger-rxjs/lib/GetV1CustomersMe';
import { ofType } from 'redux-observable';
import { EpicDeps } from '@wearejh/m2-pwa-engine/lib/types';
import { extractReferrer } from '@wearejh/m2-pwa-engine/lib/utils/general';
import { RouterState } from '@wearejh/m2-pwa-engine/lib/router/router.register';
import { catchable } from '@wearejh/swagger-rxjs/utils/ajax-helpers';

import { UserMsg, TypeMap, Actions, UserActions } from '../user.actions';

export function signInEpic(action$: Observable<any>, state$: Observable<any>, deps: EpicDeps): Observable<any> {
    const searchParam$ = state$.pipe(
        pluck<{ router: RouterState }, 'router', 'location', 'search'>('router', 'location', 'search'),
    );
    return action$.pipe(
        ofType<Actions, TypeMap['User.SignIn']>('User.SignIn'),
        withLatestFrom(searchParam$),
        switchMap(([{ payload }, search]) => {
            const { beforeActions = [], afterActions = [], onErrorActions = [], allowRedirects } = payload;
            const ajaxPayload = {
                username: payload.username,
                password: payload.password,
            };

            const referrer = search && extractReferrer(search);
            const nextAction$ = (() => {
                if (allowRedirects) {
                    if (referrer) {
                        return of(deps.push(referrer));
                    }
                }
                return EMPTY;
            })();

            const blockers$ = merge(
                action$.pipe(ofType<UserActions>('User.Reset')),
                action$.pipe(ofType(deps.LOCATION_CHANGE)),
            );

            return concat(
                from(beforeActions),
                getTokenAjax(ajaxPayload, deps).pipe(
                    switchMap((token: string) => {
                        return concat(
                            of(UserMsg('User.SignInSetToken', { token })),
                            // defer second call to allow token to be written to the store
                            // this will help 'listeners' of the `SignInSuccess` event
                            // since the data they might want (like the token) will be
                            // available to them
                            defer(() =>
                                restCustomerMe(deps).pipe(
                                    mergeMap((info) =>
                                        concat(
                                            of(UserMsg('User.SignInSuccess', info)),
                                            of(UserMsg('User.Persist')),
                                            nextAction$,
                                            from(afterActions),
                                        ),
                                    ),
                                ),
                            ),
                        );
                    }),
                    takeUntil(blockers$),
                ),
            ).pipe(
                catchable((e) => {
                    return concat(of(UserMsg('User.SignInError', e.error)), from(onErrorActions));
                }),
            );
        }),
    );
}
